import { useMemo, useCallback, Fragment } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { ChartHeader, ChartTooltip, ChartLegend, CustomLabel } from './components';
import useChartData from './hooks/useChartData';
import { CHART_CONFIG, CHART_MARGINS } from './utils/constants';
import { formatNumber } from '../../utils/formatters';
import './index.css'

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    background: 'white',
    borderRadius: 8,
    border: '1px solid #E6E9F0',
    overflow: 'hidden',
    position: 'relative',
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  chartContent: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
}));

const CombinedBarChartRecharts = ({ metrics, onMetricClick, isComparisonMode = false }) => {
  const classes = useStyles();
  const { en: { populationHealth } } = useSelector((state) => state.i18n);
  const chartData = useChartData(metrics, isComparisonMode);

  const handleClick = useCallback((data, index) => {
    if (onMetricClick && data.originalMetric) {
      onMetricClick(data.originalMetric);
    }
  }, [onMetricClick]);

  const handleChartClick = useCallback((data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const clickedData = data.activePayload[0].payload;
      if (onMetricClick && clickedData.originalMetric) {
        onMetricClick(clickedData.originalMetric);
      }
    }
  }, [onMetricClick]);

  // Custom tick component for XAxis with simple truncation
  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;

    const smartTruncate = (text, maxLength = 20) => {
      if (!text || text.length <= maxLength) return text;

      // Clean text from punctuation at the end
      const cleanText = text.replace(/[,;.!?]+$/, '');

      // Split by spaces and also by common separators
      const words = cleanText.split(/[\s,;]+/).filter(word => word.length > 0);

      // Check for patterns in any case (single word or multiple)
      const fullText = cleanText.toLowerCase();

      // Medical/health pattern matching
      if (fullText.includes('psychological') && fullText.includes('distress')) {
        return 'Psych Distress';
      }
      if (fullText.includes('psychological')) return 'Psychological';
      if (fullText.includes('physical')) return 'Physical';
      if (fullText.includes('social')) return 'Social';
      if (fullText.includes('financial')) return 'Financial';
      if (fullText.includes('emotional')) return 'Emotional';
      if (fullText.includes('mental')) return 'Mental Health';
      if (fullText.includes('distress')) return 'Distress';

      // If single word is too long after pattern check
      if (words.length === 1) {
        return words[0].substring(0, maxLength);
      }

      // For multiple words, prioritize meaningful ones
      const meaningfulWords = [];
      const skipWords = ['and', 'or', 'the', 'a', 'an', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];

      for (const word of words) {
        if (!skipWords.includes(word.toLowerCase()) && word.length > 0) {
          meaningfulWords.push(word);
        }
      }

      // Try to fit meaningful words
      let result = '';
      for (const word of meaningfulWords) {
        const newResult = result ? result + ' ' + word : word;
        if (newResult.length <= maxLength) {
          result = newResult;
        } else {
          break;
        }
      }

      return result || meaningfulWords[0] || cleanText.substring(0, maxLength);
    };

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#374151"
          fontSize="11"
          fontWeight="500"
          letterSpacing="0.01em"
          transform="rotate(-45)"
        >
          {smartTruncate(payload.value)}
        </text>
      </g>
    );
  };

  // Custom cursor component that handles clicks
  const CustomCursor = (props) => {
    const { x, y, width, height, payload } = props;

    const handleCursorClick = (e) => {
      e.stopPropagation();

      if (payload && payload.length > 0 && payload[0].payload) {
        const clickedData = payload[0].payload;

        if (onMetricClick && clickedData.originalMetric) {
          onMetricClick(clickedData.originalMetric);
        }
      }
    };

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(0, 0, 0, 0.05)"
        stroke="rgba(0, 0, 0, 0.1)"
        strokeWidth={1}
        opacity={0.8}
        style={{ cursor: 'pointer' }}
        onClick={handleCursorClick}
        onMouseEnter={() => {}}
        onMouseLeave={() => {}}
      />
    );
  };

  // Memoized cells for single mode
  const singleModeCells = useMemo(() => {
    return chartData.map((entry, index) => (
      <Cell
        key={`single-${entry.originalMetric?.id || index}`}
        fill={`url(#gradient-${index})`}
      />
    ));
  }, [chartData]);

  // Memoized cells for comparison mode
  const comparisonCells = useMemo(() => {
    const baselineCells = chartData.map((entry, index) => (
      <Cell
        key={`baseline-${entry.originalMetric?.id || index}`}
        fill={`url(#gradientBaseline-${index})`}
      />
    ));

    const followupCells = chartData.map((entry, index) => (
      <Cell
        key={`followup-${entry.originalMetric?.id || index}`}
        fill={`url(#gradient-${index})`}
      />
    ));

    return { baselineCells, followupCells };
  }, [chartData]);

  // Memoized bars rendering
  const bars = useMemo(() => {
    if (isComparisonMode) {
      return (
        <>
          <Bar
            dataKey="baseline"
            cursor="pointer"
            onClick={handleClick}
            animationDuration={300}
            radius={[6, 6, 0, 0]}
            name="Baseline"
            filter="url(#subtle3d)"
          >
            {comparisonCells.baselineCells}
            <LabelList content={CustomLabel} />
          </Bar>
          <Bar
            dataKey="followup"
            cursor="pointer"
            onClick={handleClick}
            animationDuration={300}
            radius={[6, 6, 0, 0]}
            name="Follow-up"
            filter="url(#subtle3d)"
          >
            {comparisonCells.followupCells}
            <LabelList content={CustomLabel} />
          </Bar>
        </>
      );
    } else {
      return (
        <Bar
          dataKey="value"
          cursor="pointer"
          onClick={handleClick}
          animationDuration={300}
          radius={[6, 6, 0, 0]}
          filter="url(#subtle3d)"
        >
          {singleModeCells}
          <LabelList content={CustomLabel} />
        </Bar>
      );
    }
  }, [isComparisonMode, handleClick, singleModeCells, comparisonCells]);

  return (
    <Box className={classes.chartContainer}>
      <ChartHeader
        title="Population Health Metrics"
        subtitle="Click on any bar to view detailed patient information"
      />

      <div className={classes.chartContent} style={{ height: CHART_CONFIG.CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={CHART_MARGINS}
            barCategoryGap={CHART_CONFIG.BAR_CATEGORY_GAP}
            barGap={CHART_CONFIG.BAR_GAP}
            onClick={handleChartClick}
          >
            <defs>
              {/* Subtle 3D shadow filter for narrow bars */}
              <filter id="subtle3d" x="-20%" y="-20%" width="140%" height="140%">
                {/* Outer drop shadow - minimal and centered */}
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="2"
                  floodColor="rgba(0,0,0,0.12)"
                  result="dropshadow"
                />

                {/* Inner shadow - very subtle */}
                <feFlood floodColor="rgba(0,0,0,0.08)" result="innerflood"/>
                <feComposite in="innerflood" in2="SourceGraphic" operator="in" result="innercomp"/>
                <feGaussianBlur in="innercomp" stdDeviation="1" result="innerblur"/>
                <feOffset in="innerblur" dx="0" dy="1" result="inneroffset"/>
                <feComposite in="dropshadow" in2="inneroffset" operator="over" result="combined"/>

                {/* Top highlight for 3D effect */}
                <feFlood floodColor="rgba(255,255,255,0.1)" result="highlight"/>
                <feComposite in="highlight" in2="SourceGraphic" operator="in" result="highlightcomp"/>
                <feOffset in="highlightcomp" dx="0" dy="-0.5" result="highlightoffset"/>
                <feComposite in="combined" in2="highlightoffset" operator="over"/>
              </filter>
              {/* Gradients for each metric color */}
              {chartData.map((entry, index) => (
                <Fragment key={`gradient-${index}`}>
                  <linearGradient id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={entry.color} stopOpacity="1"/>
                    <stop offset="100%" stopColor={entry.color} stopOpacity="0.85"/>
                  </linearGradient>
                  <linearGradient id={`gradientBaseline-${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={entry.color} stopOpacity="0.6"/>
                    <stop offset="100%" stopColor={entry.color} stopOpacity="0.45"/>
                  </linearGradient>
                </Fragment>
              ))}
            </defs>
            <CartesianGrid
              strokeDasharray="2 4"
              stroke="#E2E8F0"
              strokeOpacity={0.6}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              height={100}
              tick={<CustomXAxisTick />}
              interval={0}
              tickLine={{
                stroke: '#CBD5E1',
                strokeWidth: 1.5,
                strokeDasharray: '0'
              }}
              axisLine={{
                stroke: '#CBD5E1',
                strokeWidth: 1,
                strokeOpacity: 0.7
              }}
              allowDecimals={false}
              tickMargin={8}
            />
            <YAxis
              label={{
                value: populationHealth?.numberOfPatients || 'Number of Patients',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#374151',
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textAnchor: 'middle'
                },
              }}
              tickFormatter={(value) => formatNumber(value)}
              tick={{
                fontSize: 11,
                fill: '#374151',
                fontWeight: 500,
                letterSpacing: '0.01em'
              }}
              tickLine={{
                stroke: '#CBD5E1',
                strokeWidth: 1.5,
                strokeDasharray: '0'
              }}
              axisLine={{
                stroke: '#CBD5E1',
                strokeWidth: 1,
                strokeOpacity: 0.7
              }}
              width={65}
              allowDecimals={false}
              tickMargin={6}
            />
            <Tooltip
              content={<ChartTooltip isComparisonMode={isComparisonMode} />}
              animationDuration={200}
              cursor={<CustomCursor />}
            />
            {bars}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ChartLegend isComparisonMode={isComparisonMode} />
    </Box>
  );
};

export default CombinedBarChartRecharts;
